import { choraleNotes } from './chorales';
import { sonataNotes, sonataPiece } from './sonatas';
import { CADENCE_PATH } from './chorales/derive';
import { COUNTED, SONATA_ENTRIES, corpusEntry, VARIATION_ENTRIES, PRELUDE_ENTRIES, PIANO_ENTRIES } from './chorales/corpusIndex';
import type { ExcerptRef } from './chorales/corpusTypes';
import { PIECES } from './index';
import type { PathDef, PieceData } from './types';
import { Key, keyName, parseKey } from '../music/key';
import { barStart, barEnd, NoteEvent } from '../music/piece';
import { mod, pcNumber } from '../music/pitch';
import type { PlayNote } from '../audio/player';

/**
 * Real instances of each path, found by rule across every piece in the atlas.
 * Cadences come from the fermata analysis of the chorales; schemata and sequences from the
 * path recognizers; hand-annotated moments of the flagship pieces are included as they are.
 * Chorale data comes from the corpus index, so nothing is derived at run time here.
 */

export interface Instance {
  pieceId: string;
  kind: PieceData['kind'];
  title: string;
  catalogue: string;
  start: number;
  end: number;
  key: Key;
  momentId?: string;
  label: string;
  /** Semitones to move this excerpt by, instead of moving it to C major / A minor. */
  shift?: number;
}

let index: Map<string, Instance[]> | null = null;

function phraseOf(phrases: Array<[number, number]>, start: number, end: number): number {
  return phrases.findIndex(([a, b]) => a <= start + 1e-6 && b >= end - 1e-6);
}

function build(): Map<string, Instance[]> {
  const map = new Map<string, Instance[]>();
  const add = (path: string, inst: Instance) => {
    const list = map.get(path) ?? [];
    if (!list.some((x) => x.pieceId === inst.pieceId && Math.abs(x.start - inst.start) < 1e-6)) list.push(inst);
    map.set(path, list);
  };
  for (const piece of Object.values(PIECES)) {
    for (const m of piece.moments) {
      for (const p of m.paths) {
        const start = m.start ?? barStart(piece.measures, m.from);
        const end = m.end ?? barEnd(piece.measures, m.to);
        add(p, { pieceId: piece.id, kind: piece.kind, title: piece.title, catalogue: piece.catalogue, start, end, key: parseKey(piece.homeKey), momentId: m.id, label: `bars ${m.from}–${m.to}` });
      }
    }
  }
  for (const e of [...COUNTED, ...PRELUDE_ENTRIES, ...PIANO_ENTRIES, ...SONATA_ENTRIES, ...VARIATION_ENTRIES]) {
    const base = { pieceId: e.id, kind: (e.kind ?? 'chorale') as PieceData['kind'], title: e.title, catalogue: e.composer ? `${e.composer}, ${e.catalogue}` : e.catalogue };
    // Chorale moments are phrases; sonata moments are form sections.
    const momentAt = (t: number, phrase: number) => {
      if (e.sections?.length) {
        const j = e.sections.findIndex(([a, b]) => t >= a - 1e-6 && t < b - 1e-6);
        return j >= 0 ? { id: `section-${j + 1}`, name: e.sections[j][2] } : undefined;
      }
      return phrase >= 0 ? { id: `phrase-${phrase + 1}`, name: `phrase ${phrase + 1}` } : undefined;
    };
    e.cadences.forEach(([at, dur, kind, key, approach], i) => {
      const path = CADENCE_PATH[kind];
      if (!path) return;
      const phrase = e.phrases[i];
      // From one beat before the approach chord to the end of the arrival chord (at most a bar's worth).
      const start = Math.max(phrase?.[0] ?? 0, approach - 1);
      const k = parseKey(key);
      const m = momentAt(at, i);
      add(path, { ...base, start, end: at + Math.min(dur, 4), key: k, momentId: m?.id, label: `${m?.name ?? `phrase ${i + 1}`}, ${keyName(k)}` });
    });
    for (const [path, start, end, key] of e.matches) {
      const i = phraseOf(e.phrases, start, end);
      const k = parseKey(key);
      const m = momentAt(start, i);
      add(path, { ...base, start, end, key: k, momentId: m?.id, label: `${m?.name ?? 'passage'}, ${keyName(k)}` });
    }
  }
  return map;
}

export function instancesOf(path: PathDef | string): Instance[] {
  if (!index) index = build();
  return index.get(typeof path === 'string' ? path : path.id) ?? [];
}

/** An excerpt reference from the corpus index as an Instance. */
export function instanceOf(ref: ExcerptRef, label?: string): Instance {
  const [pieceId, start, end, key] = ref;
  const e = corpusEntry(pieceId);
  const piece = PIECES[pieceId];
  const k = parseKey(key);
  const phrases = e?.phrases ?? [];
  const i = phraseOf(phrases, start + 1e-3, start + 1e-3);
  // Sonata moments are form sections, chorale moments are phrases.
  const sec = e?.sections?.length ? e.sections.findIndex(([a, b]) => start >= a - 1e-6 && start < b - 1e-6) : -1;
  return {
    pieceId,
    kind: piece ? piece.kind : e?.kind ?? 'chorale',
    title: e?.title ?? piece?.title ?? pieceId,
    catalogue: e ? (e.composer ? `${e.composer}, ${e.catalogue}` : e.catalogue) : piece?.catalogue ?? '',
    start,
    end,
    key: k,
    momentId: e?.sections?.length ? (sec >= 0 ? `section-${sec + 1}` : undefined) : i >= 0 ? `phrase-${i + 1}` : undefined,
    label: label ?? `${e?.sections?.length && sec >= 0 ? e.sections[sec][2] : i >= 0 ? `phrase ${i + 1}` : `from beat ${Math.round(start) + 1}`}, ${keyName(k)}`,
  };
}

/**
 * Up to `n` items spread evenly over a list (first, last and evenly in between), so a short
 * playlist samples the whole corpus rather than its first few chorales.
 */
export function spread<T>(list: T[], n: number): T[] {
  if (list.length <= n) return list;
  const out: T[] = [];
  for (let i = 0; i < n; i++) out.push(list[Math.round((i * (list.length - 1)) / (n - 1))]);
  return out;
}

/** Semitones that move a key's tonic to C (major) or A (minor), by the shortest way. */
export function normalizeShift(key: Key): number {
  const target = key.mode === 'major' ? 0 : 9;
  let s = mod(target - pcNumber(key.tonic), 12);
  if (s > 6) s -= 12;
  return s;
}

const notesCache = new Map<string, NoteEvent[]>();
function notesOf(pieceId: string): NoteEvent[] {
  let notes = notesCache.get(pieceId);
  if (!notes) {
    notes = PIECES[pieceId]?.notes ?? choraleNotes(pieceId) ?? sonataNotes(pieceId) ?? [];
    notesCache.set(pieceId, notes);
  }
  return notes;
}

/** Notes of an instance as playable events starting at `offset`, optionally moved to C / A minor. */
export function instanceNotes(inst: Instance, offset: number, normalize: boolean): { notes: PlayNote[]; length: number } {
  const shift = inst.shift ?? (normalize ? normalizeShift(inst.key) : 0);
  const notes: PlayNote[] = notesOf(inst.pieceId)
    .filter((n) => n.onset < inst.end - 1e-6 && n.onset + n.dur > inst.start + 1e-6)
    .map((n) => {
      const s = Math.max(n.onset, inst.start);
      const e = Math.min(n.onset + n.dur, inst.end);
      return { onset: offset + s - inst.start, dur: e - s, midi: n.midi + shift, line: n.line, velocity: n.line === 0 ? 0.8 : n.line === 3 ? 0.78 : 0.62 };
    });
  return { notes, length: inst.end - inst.start };
}

/** A list of instances as one playlist, with gaps between them. */
export function playlist(
  list: Instance[],
  normalize: boolean,
  gap = 1.5,
): { notes: PlayNote[]; total: number; segments: Array<{ inst: Instance; offset: number; length: number }>; tempoMap: Array<{ at: number; factor: number }> } {
  let t = 0;
  const notes: PlayNote[] = [];
  const segments: Array<{ inst: Instance; offset: number; length: number }> = [];
  const tempoMap: Array<{ at: number; factor: number }> = [];
  for (const inst of list) {
    const x = instanceNotes(inst, t, normalize);
    notes.push(...x.notes);
    segments.push({ inst, offset: t, length: x.length });
    tempoMap.push(...speedOf(inst, t));
    t += x.length + gap;
  }
  return { notes, total: Math.max(0, t - gap), segments, tempoMap };
}

/**
 * How fast an excerpt goes, relative to the tempo control (which is set in chorale beats): a
 * sonata movement, variation or prelude at its own listening speed, with its own changes inside
 * the excerpt (an Adagio variation slows down here too); chorales and the keyboard pieces as set.
 */
export function speedOf(inst: Instance, offset: number): Array<{ at: number; factor: number }> {
  if (inst.kind !== 'sonata' && inst.kind !== 'variations' && inst.kind !== 'prelude') return [{ at: offset, factor: 1 }];
  const p = sonataPiece(inst.pieceId);
  if (!p) return [{ at: offset, factor: 1 }];
  const base = p.tempo / 66;
  let f = 1;
  for (const x of p.tempoMap ?? []) if (x.at <= inst.start + 1e-6) f = x.factor;
  const out = [{ at: offset, factor: base * f }];
  for (const x of p.tempoMap ?? []) if (x.at > inst.start + 1e-6 && x.at < inst.end - 1e-6) out.push({ at: offset + x.at - inst.start, factor: base * x.factor });
  return out;
}
